import { createFileRoute } from '@tanstack/react-router'
import { useEffect, useRef, useState } from 'react'
import { MdAdd, MdClose, MdError } from 'react-icons/md'
import { useAssistant, useAssistantPromptChangelog, useUpdateAssistant } from '~/api'
import { AppHeading } from '~/components/educator/app/AppHeading'
import { PromptRevisions } from '~/components/educator/app/PromptRevisions'
import { Button, Input, Label, Textarea } from '~ui'

export const Route = createFileRoute('/educator/courses/$courseId/apps/$appId/prompt')({
	component: Prompt,
})

function Prompt() {
	const { appId } = Route.useParams()
	const app = useAssistant(appId)
	const { mutate: updateApp } = useUpdateAssistant()
	const { data: changeLog } = useAssistantPromptChangelog()

	const [prompt, setPrompt] = useState<string | undefined>()
	const [conversationStarters, setConversationStarters] = useState<string[] | undefined>()
	const bottomRef = useRef<HTMLDivElement>(null)

	useEffect(() => {
		if (app) {
			setPrompt(app.promptTemplates?.[promptTemplateId]?.promptAiToKnow ?? '')
			const starters = app.conversationStarters ?? []
			setConversationStarters(starters.length === 0 ? [''] : starters)
		}
	}, [app])

	const haveConversationStartersChanged = () => {
		if (!app || !conversationStarters) return false

		const originalStarters = app.conversationStarters ?? []
		const currentStarters = conversationStarters.filter((starter) => starter !== '')

		if (originalStarters.length !== currentStarters.length) return true

		return originalStarters.some((starter, index) => starter !== currentStarters[index])
	}

	const promptTemplateId = Object.keys(app?.promptTemplates ?? {}).length > 1 ? 'SOCRATIC' : 'default'

	const hasPromptChanged = prompt !== app?.promptTemplates?.[promptTemplateId]?.promptAiToKnow
	const hasChanges = hasPromptChanged || haveConversationStartersChanged()

	return (
		<>
			<AppHeading>
				<div className="flex items-center gap-[10px]">
					{changeLog && changeLog.length > 0 && <PromptRevisions revisions={changeLog} />}
					<Button
						onClick={() => {
							if (app == null) return
							updateApp({
								...app,
								promptTemplates: {
									default: app.promptTemplates?.default ?? { promptAiToKnow: '' },
									[promptTemplateId]: {
										promptAiToKnow: prompt ?? '',
									},
								},
								conversationStarters: conversationStarters?.filter((starter) => starter !== ''),
							})
						}}
						disabled={!hasChanges}
					>
						Update prompt
					</Button>
				</div>
			</AppHeading>

			<h1 className="mb-[4px]">Prompt {app?.isGlobal && 'enhancements'}</h1>
			<p className="max-w-text mb-[24px]">The app's functionality is shaped by your prompts. Use this section to guide the LLM's behaviour and enhance the student experience.</p>

			{app?.isTemplate !== true && (
				<div
					className="max-w-text bg-yellow-25 text-navy-100 mb-[24px] flex items-start gap-[10px] p-[12px]"
					role="alert"
				>
					<div className="flex size-[32px] shrink-0 items-center justify-center bg-yellow-50">
						<MdError
							className="size-[20px]"
							aria-hidden
						/>
						<div className="sr-only">Warning: </div>
					</div>
					<div className=" ">
						Updating the prompt will change how the AI responds to and interacts with students. Test the responses thoroughly before granting students access to ensure they meet your expectations.
					</div>
				</div>
			)}

			<Textarea
				value={prompt}
				id="prompt"
				onChange={(e) => {
					setPrompt(e.target.value)
				}}
				className="h-[412px]"
				disabled={app?.isTemplate === true}
			/>
			{app?.promptTemplates?.SOCRATIC == null && (
				<>
					<h3 className="text-navy-100 font-600 mt-[24px] mb-[4px] text-[20px]">Conversation starters</h3>
					<p className="max-w-text mb-[24px]">
						Conversation starters help students begin a chat if they’re not sure what to ask. They appear as buttons in the chat interface each time a new thread is started.
					</p>

					<div className="flex flex-col gap-[12px]">
						{conversationStarters?.map((starter, index) => {
							return (
								<div key={index}>
									<Label className="mb-[8px] text-[18px]">Button {['One', 'Two', 'Three', 'Four', 'Five', 'Six'][index]}</Label>
									<div className="flex items-center gap-[12px]">
										<Input
											value={starter}
											className="max-w-text w-full"
											onChange={(e) => {
												const newConversationStarters = [...conversationStarters]
												newConversationStarters[index] = e.target.value
												setConversationStarters(newConversationStarters)
											}}
											disabled={app?.isTemplate === true}
										/>
										{conversationStarters.length > 1 && !app?.isTemplate && (
											<Button
												variant="ghost"
												className="flex size-[32px] items-center justify-center"
												onClick={() => {
													const newConversationStarters = [...conversationStarters]
													newConversationStarters.splice(index, 1)
													setConversationStarters(newConversationStarters)
												}}
											>
												<MdClose
													className="text-navy-100 size-[19px]"
													aria-hidden
												/>
												<div className="sr-only">Remove button</div>
											</Button>
										)}
									</div>
								</div>
							)
						})}
					</div>
					<Button
						variant="ghost"
						className="font-700 mt-[24px] text-[14px]"
						onClick={() => {
							setConversationStarters([...(conversationStarters ?? []), ''])
							setTimeout(() => {
								bottomRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end', inline: 'nearest' })
							}, 100)
						}}
						disabled={app?.isTemplate === true || conversationStarters?.length === 6 || conversationStarters?.[conversationStarters.length - 1]?.trim().length === 0}
					>
						<MdAdd className="size-[16px]" /> <div className="">Add button</div>
					</Button>
				</>
			)}
			<div
				ref={bottomRef}
				className="mt-[12px] h-[1px]"
			/>
		</>
	)
}
